import type { OptionsDocument, QuoteDocument, QuoteFile } from 'paintscout'
import { getItem, updateItem } from '../../items'
import { updateFiles } from '../update-files/update-files'
import uniq from 'lodash/uniq'
import { updateGroup } from '../../groups'
import { isInAreaGroup } from '../../area-substrates'

/**
 * Update by append or replacing files for a given itemKey and return updated quote
 */
export function updateFilesForItem(args: {
  quote: QuoteDocument
  options: OptionsDocument
  itemKey: string
  files: QuoteFile[]
  append?: boolean
}): QuoteDocument {
  const { quote, options, itemKey, files, append } = args

  const item = getItem({ quote, options, key: itemKey })

  let fileKeys = uniq(files.map((file) => file.key))
  if (append) {
    fileKeys = uniq([...item.files, ...fileKeys])
  }
  const removedKeys = item?.files?.filter((key) => !fileKeys.includes(key)) ?? []

  const updatedItem = {
    ...item,
    // @ts-ignore - remove legacy images order
    images: [],
    files: fileKeys
  }

  const updatedItemQuote = updateItem({ quote, options, item: updatedItem })
  const parentGroup = isInAreaGroup({ quote: updatedItemQuote, areaKey: itemKey })
  const itemIsGroup = quote?.groups?.[itemKey] ?? null

  if (!parentGroup && !itemIsGroup) {
    return updateFiles({
      quote: updatedItemQuote,
      files,
      removedFiles: removedKeys
    })
  }
  const updatedFilesGroup = {
    ...(parentGroup || itemIsGroup),
    files: [...[...(parentGroup?.files ?? itemIsGroup?.files ?? null)].filter(Boolean), ...fileKeys].filter(
      (key) => !removedKeys.includes(key)
    )
  }

  const updatedGroupQuote = updateGroup({
    quote: updatedItemQuote,
    options,
    group: updatedFilesGroup
  })

  if (itemIsGroup && removedKeys) {
    // if item is group we need to remove the file from the item in the group too
    for (const itemKey of updatedFilesGroup.children) {
      const item = getItem({ quote: updatedGroupQuote, options, key: itemKey })
      const hasFile = item?.files?.some((key) => removedKeys.includes(key)) ?? false
      if (hasFile) {
        const updatedItem = {
          ...item,
          files: item.files.filter((key) => !removedKeys.includes(key))
        }
        const updatedCleanGroupItemQuote = updateItem({ quote: updatedGroupQuote, options, item: updatedItem })
        return updateFiles({
          quote: updatedCleanGroupItemQuote,
          files,
          removedFiles: removedKeys
        })
      }
    }
  }

  return updateFiles({
    quote: updatedGroupQuote,
    files,
    removedFiles: removedKeys
  })
}
